// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import Dropzone, { DropzoneProps, DropEvent, FileRejection, DropzoneRef } from 'react-dropzone';
import { withStyles } from "tss-react/mui";
import { showNotification, NotificationType } from '@shared/components/Notification';
import { parseBytes } from '@shared/utils/common';

// prop interface for the main class.
export interface FileUploadProps {
  className?: string;
  config: DropzoneProps;
  placeholder?: React.ReactNode;
  successMessage?: string;
  onUpload: (acceptedFiles: File[] | File) => any;
}

// Class to implement the browse button for importing file.
class FileUpload extends React.Component<FileUploadProps> {
  private dropzoneRef = React.createRef<DropzoneRef>();

  private onDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent): void => {
    const { onUpload, config, successMessage } = this.props;

    if (rejectedFiles.length) {
      const { maxSize, accept } = config;
      const { amount, unit } = parseBytes(maxSize ?? 0);

      showNotification(
        `You can upload files which are ${accept ? Object.keys(accept).join(', ') : ''} and up to ${amount}${unit}`,
        NotificationType.error
      );

      return;
    }

    if (config.multiple) {
      onUpload(acceptedFiles);

      return;
    }

    onUpload(acceptedFiles[0]);

    if (successMessage) {
      showNotification(successMessage);
    }
  }

  render() {
    const { placeholder, config } = this.props;
    const classes = withStyles.getClasses(this.props);

    return (
      <Dropzone
        ref={this.dropzoneRef}
        onDrop={this.onDrop}
        {...config}
      >
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps()} className={classes.root}>
            <input {...getInputProps()} />
            {placeholder && <p className={classes.placeholder}>{placeholder}</p>}
          </div>
        )}
      </Dropzone>
    );
  }
}

const FileUploadStyled = withStyles(
  FileUpload, 
  (theme, props) => ({
    "root": {
      cursor: 'pointer',
      outline: 'none',
      display: 'flex',
      height: 120,
      width: 120,
      background: theme.palette.colors.white,
      flexDirection: 'column',
      border: `1px dashed ${theme.palette.colors.greyLight};`,
      borderRadius: '4px',
      transition: `${theme.transitions.duration.standard}s`,
      justifyContent: 'center',
      alignItems: 'center',
  
      '&:hover': {
        borderColor: theme.palette.colors.greyDark,
      }
    },
    "placeholder": {
      color: theme.palette.colors.primaryAccent,
      fontSize: '13px',
      lineHeight: 1.2
    }
  })
);

export default FileUploadStyled;